import { DOCUMENT } from '@angular/common';
import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { catchError, defaultIfEmpty, firstValueFrom, of } from 'rxjs';
import { HotjarFunction } from '../model/hotjar-function';
import { HotjarSettings } from '../model/hotjar-settings';
import { HOTJAR_SETTINGS, HotjarSettingsLoader } from './hotjar-settings.provider';
import { HotjarUserAttributesLoader } from './hotjar-user-attributes.provider';
import { HJ_WINDOW } from './hotjar-window.provider';

/**
 * This is the hotjar initializer, which should only be called once on initialization
 *
 * @param settings Hotjar Settings
 * @param document Document instance
 * @param window Window instance
 * @returns
 */
export function hotjarInitializer(
  settings: HotjarSettings,
  document: Document,
  window: (Window & { hj?: HotjarFunction; _hjSettings?: { hjid: string; hjsv: number } }) | null,
  hotjarUserAttributesLoader: HotjarUserAttributesLoader,
) {
  return () => {
    // if settings are missing, log the error and return
    if (!settings?.trackingCode) {
      console.error('Empty tracking code for Hotjar. Please provide one when importing ther HotjarModule.forRoot()');
      return;
    }

    // if settings are missing, log the error and return
    if (!window) {
      console.error('Please make sure the environment is a browser. Was not possible to access `document` instance');
      return;
    }

    // if settings are missing, log the error and return
    if (!document) {
      console.error('Please make sure the environment is a browser. Was not possible to access `window` instance');
      return;
    }

    // define the window hotjar property
    Object.defineProperty(window, 'hj', {
      value:
        window.hj ||
        function () {
          if (window.hj) {
            // eslint-disable-next-line prefer-rest-params
            (window.hj.q = window.hj.q || []).push(arguments);
          }
        },
      configurable: true,
      writable: true,
    });

    // define the window hotjar settings property. That was neccessary, because the hotjar script is using these settings
    Object.defineProperty(window, '_hjSettings', {
      value: { hjid: settings.trackingCode, hjsv: settings.version || 6 },
      configurable: true,
      writable: true,
    });

    // Create the hotjar script tag and append it to the head
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${window?._hjSettings?.hjid}.js?sv=${window?._hjSettings?.hjsv}`;

    if (settings.enabled) {
      head?.appendChild(script);

      // set user attributes, when user data are set
      hotjarUserAttributesLoader.getUserAttributes().subscribe((userAttributes) => {
        if (window.hj && userAttributes?.id) {
          window?.hj('identify', userAttributes.id, userAttributes.attributes);
        }
      });
    }
  };
}

/**
 * Provides a TOKEN to manually configure Hojtar tracking code by angular way.
 *
 * Bootstrap is blocked until the {@link HotjarSettingsLoader} emits, so that a brand-specific
 * tracking code can be resolved before the snippet is injected. The default loader emits
 * synchronously, which leaves the single-site setup unaffected.
 */
export const HJ_PROVIDER: EnvironmentProviders = provideAppInitializer(() => {
  const fallbackSettings = inject(HOTJAR_SETTINGS);
  const document = inject(DOCUMENT);
  const window = inject(HJ_WINDOW);
  const userAttributesLoader = inject(HotjarUserAttributesLoader);

  return firstValueFrom(
    inject(HotjarSettingsLoader)
      .getSettings()
      .pipe(
        catchError((error) => {
          // Never let analytics block the app: fall back to the statically configured site.
          console.error('Failed to resolve Hotjar settings, falling back to the default site.', error);
          return of(fallbackSettings);
        }),
        // A loader that completes without emitting would otherwise reject and abort bootstrap.
        defaultIfEmpty(fallbackSettings),
      ),
  ).then((settings) => hotjarInitializer(settings ?? fallbackSettings, document, window, userAttributesLoader)());
});
